export const ORIGIN = "https://standxray.com";
export const SITE_NAME = "StandXRAY";
export const DEFAULT_DESCRIPTION =
  "Verifique stands de automóveis em Portugal antes de comprar. Relatórios de risco com dados públicos e oficiais.";

export type PageMeta = {
  title: string;
  description: string;
  canonical: string;
  ogType: string;
  noindex?: boolean;
  jsonLd?: unknown[];
};

const routeAliases: Record<string, string> = {
  "/directory": "/",
  "/example": "/exemplo",
  "/verificar": "/",
  "/precos": "/pricing",

  "/metodologia": "/methodology",
  "/contacto": "/contact",
  "/contactar": "/contact",
  "/login": "/entrar",
  "/signup": "/registar",
  "/termos": "/legal",
  "/privacidade": "/legal",
};

const faqItems = [
  {
    q: "De onde vêm os dados?",
    a: "De fontes públicas e oficiais, incluindo Justiça/CITIUS, Publicações MJ, AT, Segurança Social, ASAE e Livro de Reclamações (quando existe estatística pública).",
  },
  {
    q: "Posso pesquisar por matrícula ou VIN?",
    a: "Ainda não. Nesta versão, a análise é ao stand/empresa apenas por NIF/NIPC completo. Matrícula/VIN exige integrações específicas e ficará para versões futuras.",
  },
  {
    q: "Isto substitui aconselhamento jurídico?",
    a: "Não. O relatório organiza sinais públicos para ajudar na decisão. Em casos de risco elevado, deve validar com apoio jurídico.",
  },
  {
    q: "Como desbloqueio o relatório?",
    a: "Depois da prévia gratuita, paga uma única vez em Stripe Checkout. Em Portugal, o fluxo está preparado para cartão, MB WAY e Multibanco.",
  },
];

function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: ORIGIN,
    logo: `${ORIGIN}/favicon.svg`,
    description: DEFAULT_DESCRIPTION,
  };
}

function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: ORIGIN,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${ORIGIN}/?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

function faqPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

function productSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Relatório StandXRAY",
    description:
      "Relatório completo de risco de stand automóvel com score, evidência por fonte e PDF.",
    brand: { "@type": "Organization", name: SITE_NAME },
    offers: [
      {
        "@type": "Offer",
        name: "Relatório completo",
        price: "19.99",
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
      },
    ],
  };
}

export function resolveRoute(pathname: string): string {
  const path = pathname.split(/[?#]/)[0] || "/";
  return routeAliases[path] ?? path;
}

export function getRouteRedirect(pathname: string): string | null {
  const raw = pathname.split(/[?#]/)[0] || "/";

  if (routeAliases[raw]) return routeAliases[raw];
  return null;
}

export function getPageSeo(pathname: string): PageMeta {
  const path = resolveRoute(pathname);

  switch (path) {
    case "/":
      return {
        title: `${SITE_NAME} — Verifique Stands Automóveis em Portugal`,
        description: DEFAULT_DESCRIPTION,
        canonical: ORIGIN,
        ogType: "website",
        jsonLd: [organizationSchema(), websiteSchema()],
      };

    case "/exemplo":
      return {
        title: `Exemplo de Relatório | ${SITE_NAME}`,
        description:
          "Veja um exemplo completo de relatório de risco de stand automóvel com 9 categorias de verificação.",
        canonical: `${ORIGIN}/exemplo`,
        ogType: "website",
        jsonLd: [
          organizationSchema(),
          breadcrumbSchema([
            { name: "Início", url: ORIGIN },
            { name: "Exemplo", url: `${ORIGIN}/exemplo` },
          ]),
        ],
      };

    case "/pricing":
      return {
        title: `Preços — Relatório a Partir de 19,99 € | ${SITE_NAME}`,
        description:
          "Pesquise grátis. Só paga quando quiser o relatório completo, com evidência por fonte e PDF para guardar. Sem subscrições.",
        canonical: `${ORIGIN}/pricing`,
        ogType: "website",
        jsonLd: [
          organizationSchema(),
          productSchema(),
          breadcrumbSchema([
            { name: "Início", url: ORIGIN },
            { name: "Preços", url: `${ORIGIN}/pricing` },
          ]),
        ],
      };

    case "/faq":
      return {
        title: `Perguntas Frequentes | ${SITE_NAME}`,
        description:
          "Respostas sobre fontes de dados, métodos de pesquisa, pagamento e limites dos relatórios StandXRAY.",
        canonical: `${ORIGIN}/faq`,
        ogType: "website",
        jsonLd: [
          organizationSchema(),
          faqPageSchema(),
          breadcrumbSchema([
            { name: "Início", url: ORIGIN },
            { name: "FAQ", url: `${ORIGIN}/faq` },
          ]),
        ],
      };

    case "/contact":
      return {
        title: `Contacto | ${SITE_NAME}`,
        description:
          "Entre em contacto com a equipa StandXRAY para questões sobre relatórios, pagamentos ou parcerias.",
        canonical: `${ORIGIN}/contact`,
        ogType: "website",
        jsonLd: [
          organizationSchema(),
          breadcrumbSchema([
            { name: "Início", url: ORIGIN },
            { name: "Contacto", url: `${ORIGIN}/contact` },
          ]),
        ],
      };

    case "/legal":
      return {
        title: `Informação Legal | ${SITE_NAME}`,
        description:
          "Termos de utilização, política de privacidade e limites legais dos relatórios StandXRAY.",
        canonical: `${ORIGIN}/legal`,
        ogType: "website",
        jsonLd: [organizationSchema()],
      };

    case "/methodology":
      return {
        title: `Metodologia — Como Funciona o Score | ${SITE_NAME}`,
        description:
          "Como o StandXRAY calcula o score de risco: fontes, pesos, limites e estados operacionais de cada categoria.",
        canonical: `${ORIGIN}/methodology`,
        ogType: "website",
        jsonLd: [
          organizationSchema(),
          breadcrumbSchema([
            { name: "Início", url: ORIGIN },
            { name: "Metodologia", url: `${ORIGIN}/methodology` },
          ]),
        ],
      };

    case "/entrar":
    case "/registar":
      return {
        title: path === "/entrar" ? `Entrar | ${SITE_NAME}` : `Registar | ${SITE_NAME}`,
        description: DEFAULT_DESCRIPTION,
        canonical: `${ORIGIN}${path}`,
        ogType: "website",
        noindex: true,
      };

    default:
      if (path.startsWith("/checkout/")) {
        return {
          title: `Estado do Pagamento | ${SITE_NAME}`,
          description: "Confirmação de pagamento e acesso ao relatório StandXRAY.",
          canonical: `${ORIGIN}${path}`,
          ogType: "website",
          noindex: true,
        };
      }

      if (path.startsWith("/report/")) {
        return {
          title: `Relatório de Risco | ${SITE_NAME}`,
          description:
            "Relatório de risco de stand automóvel com verificação de fontes públicas e oficiais.",
          canonical: `${ORIGIN}${path}`,
          ogType: "website",
          noindex: true,
        };
      }
      return {
        title: `${SITE_NAME} — Verifique Stands Automóveis em Portugal`,
        description: DEFAULT_DESCRIPTION,
        canonical: ORIGIN,
        ogType: "website",
      };
  }
}
